import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import {
  User, Building2, Briefcase, CalendarDays, FileSignature, MessageSquare, AlertTriangle,
  Mail, Phone, MapPin, Clock, CheckCircle2, ChevronRight,
} from 'lucide-react';
import { placementsAPI, feedbackFormsAPI } from '../lib/api';
import { toast } from 'sonner';

/**
 * PlacementDetailsDialog — one-glance view of every fact linked to a placement.
 * Fetches everything via a single /api/placements/{id}/details request.
 * Props: open, onOpenChange, placementId (falsy = closed).
 */
const StatusPill = ({ status }) => {
  const s = (status || '').toLowerCase();
  const map = {
    active: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    completed: 'bg-blue-100 text-blue-700 border-blue-200',
    pending: 'bg-amber-100 text-amber-700 border-amber-200',
    cancelled: 'bg-gray-100 text-gray-700 border-gray-200',
    draft: 'bg-gray-100 text-gray-700 border-gray-200',
    sent: 'bg-amber-100 text-amber-800 border-amber-200',
    viewed: 'bg-sky-100 text-sky-800 border-sky-200',
    signed: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    submitted: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${map[s] || 'bg-gray-100 text-gray-700 border-gray-200'}`}>
      {status || '—'}
    </span>
  );
};

const InfoRow = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-2 py-1">
    {Icon && <Icon className="w-3.5 h-3.5 text-gray-400 mt-0.5 shrink-0" />}
    <div className="flex-1 min-w-0">
      <div className="text-[11px] uppercase tracking-wide text-gray-400">{label}</div>
      <div className="text-sm text-gray-800 break-words">{value || <em className="text-gray-400">—</em>}</div>
    </div>
  </div>
);

const SectionCard = ({ icon: Icon, title, headerRight, children, tone = 'default' }) => (
  <div className={`rounded-lg border ${tone === 'warning' ? 'border-amber-300 bg-amber-50' : 'border-gray-200 bg-white'} overflow-hidden`}>
    <div className={`flex items-center justify-between px-4 py-2.5 ${tone === 'warning' ? 'bg-amber-100/50' : 'bg-gray-50'} border-b ${tone === 'warning' ? 'border-amber-200' : 'border-gray-200'}`}>
      <div className={`flex items-center gap-2 text-sm font-semibold ${tone === 'warning' ? 'text-amber-800' : 'text-ica-navy'}`}>
        {Icon && <Icon className="w-4 h-4" />} {title}
      </div>
      {headerRight}
    </div>
    <div className="px-4 py-3">{children}</div>
  </div>
);

const PlacementDetailsDialog = ({ open, onOpenChange, placementId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !placementId) { setData(null); return; }
    (async () => {
      setLoading(true);
      try {
        const res = await placementsAPI.getDetails(placementId);
        setData(res.data);
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Failed to load placement details');
        onOpenChange(false);
      } finally {
        setLoading(false);
      }
    })();
  }, [open, placementId, onOpenChange]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="placement-details-dialog"
        className="max-w-4xl max-h-[92vh] overflow-hidden flex flex-col p-0"
      >
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-gray-200">
          <DialogTitle className="flex items-center gap-2 text-ica-navy">
            <Briefcase className="w-5 h-5" /> Placement Details
          </DialogTitle>
          <DialogDescription className="sr-only">
            Full detail view of this student placement — student, restaurant, dates, assessments, contracts and timeline.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4 bg-gray-50">
          {loading && (
            <div className="py-12 text-center text-gray-500">Loading…</div>
          )}
          {!loading && data && (
            <>
              {data.is_orphaned && (
                <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800 flex items-center gap-2" data-testid="placement-orphan-warning">
                  <AlertTriangle className="w-4 h-4 shrink-0" />
                  <span>The restaurant this placement was linked to has been deleted. Use the <strong>Fix now</strong> banner on the Placements page to reassign or delete this orphan.</span>
                </div>
              )}

              {/* Placement summary strip */}
              <div className="rounded-lg border border-gray-200 bg-white px-4 py-3 flex flex-wrap items-center gap-x-6 gap-y-2">
                <div className="flex items-center gap-2 min-w-0">
                  <User className="w-4 h-4 text-ica-navy shrink-0" />
                  <span className="font-semibold text-ica-navy truncate">{data.student?.name || 'Unknown student'}</span>
                  <span className="text-xs text-gray-500">→</span>
                  <Building2 className="w-4 h-4 text-ica-navy shrink-0" />
                  <span className="font-semibold text-ica-navy truncate">{data.restaurant?.name || data.placement.restaurant_name_snapshot || 'Unknown restaurant'}</span>
                </div>
                <div className="text-xs text-gray-500 flex items-center gap-1"><CalendarDays className="w-3.5 h-3.5" /> {data.placement.start_date} → {data.placement.end_date}</div>
                <div className="ml-auto"><StatusPill status={data.placement.status} /></div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Student */}
                <SectionCard icon={User} title="Student">
                  <InfoRow label="Full name" value={data.student?.name} />
                  <InfoRow label="Student #" value={data.student?.student_number} />
                  <InfoRow label="Year / Course" value={[data.student?.year_of_study, data.student?.course_type, data.student?.course_length].filter(Boolean).join(' · ')} />
                  <InfoRow icon={Mail} label="Email" value={data.student?.email} />
                  <InfoRow icon={Phone} label="Phone" value={data.student?.phone} />
                </SectionCard>

                {/* Restaurant */}
                <SectionCard
                  icon={Building2}
                  title="Restaurant"
                  tone={data.is_orphaned ? 'warning' : 'default'}
                >
                  {data.restaurant ? (
                    <>
                      <InfoRow label="Name" value={data.restaurant.name} />
                      <InfoRow label="Contact person" value={data.restaurant.contact_person} />
                      <InfoRow icon={Mail} label="Chef email" value={data.restaurant.email} />
                      <InfoRow icon={Mail} label="HR / admin email" value={data.restaurant.hr_email} />
                      <InfoRow icon={Phone} label="Phone" value={data.restaurant.phone} />
                      {data.restaurant.phone_2 && <InfoRow icon={Phone} label="Phone (secondary)" value={data.restaurant.phone_2} />}
                      <InfoRow icon={MapPin} label="Address" value={data.restaurant.address} />
                      <InfoRow label="Cuisine" value={data.restaurant.cuisine_type} />
                    </>
                  ) : (
                    <div className="text-sm text-amber-800">
                      <div className="font-semibold">Restaurant deleted</div>
                      <div className="text-xs mt-1">Last known name: <em>{data.placement.restaurant_name_snapshot || '—'}</em></div>
                      <div className="text-[11px] font-mono text-amber-700 break-all mt-1">id: {data.placement.restaurant_id}</div>
                    </div>
                  )}
                </SectionCard>

                {/* Placement */}
                <SectionCard icon={Briefcase} title="Placement">
                  <InfoRow label="Position" value={data.placement.position} />
                  <InfoRow icon={CalendarDays} label="Start date" value={data.placement.start_date} />
                  <InfoRow icon={CalendarDays} label="End date" value={data.placement.end_date} />
                  <InfoRow label="Status" value={<StatusPill status={data.placement.status} />} />
                  <InfoRow icon={MessageSquare} label="Notes" value={data.placement.notes} />
                  {data.placement.feedback && <InfoRow label="Legacy feedback" value={data.placement.feedback} />}
                </SectionCard>

                {/* Assessments */}
                <SectionCard
                  icon={CheckCircle2}
                  title="Assessments"
                  headerRight={
                    <span className="text-xs text-gray-500">
                      {data.assessments.completed}/{data.assessments.total} completed
                    </span>
                  }
                >
                  {data.assessments.forms.length === 0 ? (
                    <div className="text-sm text-gray-500 italic">No assessments scheduled.</div>
                  ) : (
                    <ul className="space-y-1" data-testid="placement-assessments-list">
                      {data.assessments.forms.map((f) => (
                        <li key={f.id} className="flex items-center justify-between gap-2 py-1 text-sm">
                          <div className="flex items-center gap-2 min-w-0 flex-1">
                            <span className="w-5 h-5 shrink-0 rounded-full bg-ica-navy text-white text-[10px] font-bold flex items-center justify-center">{f.assessment_number}</span>
                            <div className="min-w-0 flex-1">
                              <div className="text-gray-800 truncate">Assessment #{f.assessment_number}</div>
                              <div className="text-[11px] text-gray-400 flex items-center gap-1.5">
                                <span>Due</span>
                                {f.status === 'submitted' ? (
                                  <span>{f.due_date || '—'}</span>
                                ) : (
                                  <input
                                    type="date"
                                    data-testid={`details-due-${f.id}`}
                                    defaultValue={f.due_date || ''}
                                    onBlur={async (e) => {
                                      const v = e.target.value;
                                      if (!v || v === f.due_date) return;
                                      try {
                                        await feedbackFormsAPI.patch(f.id, { due_date: v });
                                        toast.success('Due date updated');
                                        // reflect locally so the value sticks after blur
                                        f.due_date = v;
                                      } catch (err) {
                                        toast.error(err.response?.data?.detail || 'Update failed');
                                        e.target.value = f.due_date || '';
                                      }
                                    }}
                                    className="border border-gray-200 rounded px-1.5 py-0 h-5 text-[11px] w-32"
                                  />
                                )}
                                {f.reminder_sent_at && <span>· reminder sent</span>}
                              </div>
                            </div>
                          </div>
                          <StatusPill status={f.status} />
                        </li>
                      ))}
                    </ul>
                  )}
                </SectionCard>
              </div>

              {/* Documents (signed / outstanding overview) */}
              <SectionCard
                icon={FileSignature}
                title="Documents overview"
                headerRight={
                  <span className="text-xs text-gray-500">
                    {data.documents.filter(d => d.status === 'signed').length}/{data.documents.length} signed
                  </span>
                }
              >
                <ul className="divide-y divide-gray-100" data-testid="placement-documents-list">
                  {data.documents.map((d, i) => (
                    <li key={i} className="py-2 flex items-center justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-gray-800">{d.label}</div>
                        <div className="text-[11px] text-gray-500">
                          Signer: {d.expected_signer}
                          {d.signed_at ? ` · signed ${d.signed_at.slice(0,10)}${d.signed_by ? ` by ${d.signed_by}` : ''}` : ''}
                          {!d.signed_at && d.sent_at ? ` · sent ${d.sent_at.slice(0,10)}` : ''}
                        </div>
                      </div>
                      <StatusPill status={d.status === 'not_created' ? 'not created' : d.status} />
                    </li>
                  ))}
                </ul>
              </SectionCard>

              {/* Contracts */}
              <SectionCard
                icon={FileSignature}
                title="Contracts / Letters"
                headerRight={<span className="text-xs text-gray-500">{data.contracts.length} linked</span>}
              >
                {data.contracts.length === 0 ? (
                  <div className="text-sm text-gray-500 italic">No contracts linked to this placement yet.</div>
                ) : (
                  <ul className="divide-y divide-gray-100" data-testid="placement-contracts-list">
                    {data.contracts.map((c) => (
                      <li key={c.id} className="py-2 flex items-center justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium text-gray-800 truncate">{c.template_name}</div>
                          <div className="text-[11px] text-gray-400">
                            {c.category || 'contract'}{c.recipient_email ? ` · ${c.recipient_email}` : ''}
                            {c.sent_at ? ` · sent ${c.sent_at.slice(0,10)}` : ''}
                            {c.signed_at ? ` · signed ${c.signed_at.slice(0,10)}` : ''}
                          </div>
                        </div>
                        <StatusPill status={c.status} />
                        {c.access_token && (
                          <a
                            href={`/contract/sign/${c.access_token}`}
                            target="_blank"
                            rel="noreferrer"
                            className="text-xs font-medium text-ica-gold-dark hover:underline flex items-center gap-0.5"
                            title="Open signing view"
                          >
                            View <ChevronRight className="w-3 h-3" />
                          </a>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </SectionCard>

              {/* Timeline */}
              <SectionCard icon={Clock} title="Timeline">
                {data.timeline.length === 0 ? (
                  <div className="text-sm text-gray-500 italic">Nothing has happened yet.</div>
                ) : (
                  <ol className="space-y-2" data-testid="placement-timeline">
                    {data.timeline.map((e, i) => (
                      <li key={i} className="flex items-start gap-3 text-sm">
                        <div className="mt-1 w-2 h-2 rounded-full bg-ica-gold shrink-0" />
                        <div className="flex-1">
                          <div className="text-gray-800">{e.event}</div>
                          <div className="text-[11px] text-gray-400">{(e.at || '').replace('T', ' ').slice(0, 19)}</div>
                        </div>
                      </li>
                    ))}
                  </ol>
                )}
              </SectionCard>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PlacementDetailsDialog;
